// Use the centralized API base URL from src/config.js to avoid incorrect fallbacks
import { API_BASE_URL as CONFIG_API_BASE_URL } from '../config';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || CONFIG_API_BASE_URL;

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.timeouts = new Map(); // For debounced operations
  }

  // Helper method for making HTTP requests - Enhanced version
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        ...options.headers,
        'ngrok-skip-browser-warning': 'true',
      },
      ...options,
    };

    // Handle FormData uploads
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // For non-GET requests with JSON bodies, set Content-Type
    const method = (options.method || 'GET').toUpperCase();
    if (method !== 'GET' && !(options.body instanceof FormData)) {
      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
    }

    // Add authentication if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Add CSRF token for non-GET requests
    if (method !== 'GET') {
      const csrfToken = this.getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        this.handleAuthError();
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${options.method || 'GET'}] ${endpoint}:`, error);
      throw error;
    }
  }

  // =================== UTILITY METHODS ===================
  getCSRFToken() {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrf_token='))
      ?.split('=')[1];
    const meta = document.querySelector('meta[name="csrf-token"]')?.content;
    return cookie || meta;
  }

  handleAuthError() {
    localStorage.clear();
    window.dispatchEvent(new Event('logout'));
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  /**
   * Get current user ID from JWT token stored in localStorage
   */
  getCurrentUserId() {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found in localStorage');
      return null;
    }

    try {
      // Decode JWT token to get user_id
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id || null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  // ================= CENTERS API =================
  async listCenters() {
    return this.makeRequest('/centers');
  }

  // ================= STARTUP REGISTRATION & PROFILE API (Epic 1 & 2) =================
  async registerStartup(data) {
    return this.makeRequest('/startups/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getStartupStatus(startupId) {
    return this.makeRequest(`/startups/${startupId}/status`);
  }

  async getIncompleteStartups() {
    return this.makeRequest('/startups/incomplete');
  }

  async getStartupProfile(startupId) {
    return this.makeRequest(`/startups/${startupId}/profile`);
  }

  async updateProfileSection(startupId, section, data) {
    return this.makeRequest(`/startups/${startupId}/profile/${section}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // ================= AI PROCESSING API (Epic 1) =================
  async uploadPitchDeck(startupId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.makeRequest(`/startups/${startupId}/pitch-deck/upload`, {
      method: 'POST',
      body: formData,
    });
  }

  async getPitchDeckProcessingStatus(startupId, taskId = null) {
    const endpoint = taskId
      ? `/startups/${startupId}/pitch-deck/process-status?task_id=${taskId}`
      : `/startups/${startupId}/pitch-deck/process-status`;
    return this.makeRequest(endpoint);
  }

  // ================= DOCUMENT MANAGEMENT API (Epic 3) =================
  async uploadDocument(startupId, file, documentType, onProgress) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    const url = `${this.baseURL}/startups/${startupId}/documents/upload`;
    const token = localStorage.getItem('token');
    const csrfToken = this.getCSRFToken();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('ngrok-skip-browser-warning', 'true');

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      if (csrfToken) {
        xhr.setRequestHeader('X-CSRFToken', csrfToken);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && typeof onProgress === 'function') {
          const percent = Math.round((event.loaded / event.total) * 100);
          try { onProgress(percent); } catch (_) { }
        }
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const json = JSON.parse(xhr.responseText || '{}');
              resolve(json);
            } catch (e) {
              resolve({});
            }
          } else if (xhr.status === 401) {
            this.handleAuthError();
            reject(new Error('Unauthorized'));
          } else {
            let message = `HTTP ${xhr.status}`;
            try {
              const err = JSON.parse(xhr.responseText || '{}');
              message = err.error || err.message || message;
            } catch (_) { }
            reject(new Error(message));
          }
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      xhr.send(formData);
    });
  }

  async getStartupDocuments(startupId) {
    return this.makeRequest(`/startups/${startupId}/documents`);
  }

  async uploadStartupDocument(startupId, formData) {
    return this.makeRequest(`/startups/${startupId}/documents/upload`, {
      method: 'POST',
      body: formData,
    });
  }

  async deleteStartupDocument(startupId, documentId) {
    return this.makeRequest(`/startups/${startupId}/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  // New: get available document templates
  async getDocumentTemplates(centerId = null) {
    const params = centerId ? `?center_id=${centerId}` : '';
    return this.makeRequest(`/documents/templates${params}`);
  }

  // New: generate a compliance document and return the raw Response for download
  async generateComplianceDocument(startupId, options) {
    const url = `${this.baseURL}/startups/${startupId}/export`;
    const token = localStorage.getItem('token');
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(options)
    });
    if (!res.ok) {
      let msg = 'Generation failed';
      try { const j = await res.json(); msg = j.error || j.message || msg; } catch (_) { }
      throw new Error(msg);
    }
    return res;
  }

  async verifyDocument(startupId, documentId, verificationData) {
    return this.makeRequest(`/startups/${startupId}/documents/${documentId}/verify`, {
      method: 'PUT',
      body: JSON.stringify(verificationData),
    });
  }

  async exportComplianceDocuments(startupId, exportOptions = {}) {
    return this.makeRequest(`/startups/${startupId}/export`, {
      method: 'POST',
      body: JSON.stringify(exportOptions),
    });
  }

  async getDocumentRequirements(centerId = null) {
    const params = centerId ? `?center_id=${centerId}` : '';
    return this.makeRequest(`/documents/requirements${params}`);
  }

  // ================= REVIEW PROCESS API (Epic 4) =================
  async submitStartup(startupId) {
    return this.makeRequest(`/startups/${startupId}/submit`, {
      method: 'POST',
    });
  }

  async reviewStartup(startupId, reviewData) {
    return this.makeRequest(`/startups/${startupId}/review`, {
      method: 'PUT',
      body: JSON.stringify(reviewData),
    });
  }

  async getStartupAnalytics(startupId) {
    return this.makeRequest(`/startups/${startupId}/analytics`);
  }

  // ================= PROFILE WORKFLOW API (Epic 4) =================
  async submitProfile(startupId, data = {}) {
    return this.makeRequest(`/startups/${startupId}/submit`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async resubmitProfile(startupId, revisionData = {}) {
    return this.makeRequest(`/startups/${startupId}/profile/resubmit`, {
      method: 'POST',
      body: JSON.stringify(revisionData),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async getProfileVersion(startupId, versionNumber) {
    return this.makeRequest(`/startups/${startupId}/profile/version/${versionNumber}`);
  }

  // ================= UTILITY METHODS =================

  /**
   * Auto-save profile section with debouncing - Enhanced version
   */
  async autoSaveSection(startupId, section, data, debounceMs = 1000) {
    const key = `autosave_${startupId}_${section}`;

    // Clear existing timeout
    if (this.timeouts.has(key)) {
      clearTimeout(this.timeouts.get(key));
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(async () => {
        try {
          const result = await this.updateProfileSection(startupId, section, data);
          this.timeouts.delete(key);
          resolve(result);
        } catch (error) {
          this.timeouts.delete(key);
          reject(error);
        }
      }, debounceMs);

      this.timeouts.set(key, timeoutId);
    });
  }

  /**
   * Poll for processing status with automatic retry
   */
  async pollProcessingStatus(startupId, maxAttempts = 30, intervalMs = 2000) {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;
          const status = await this.getPitchDeckProcessingStatus(startupId);

          if (status.status === 'completed' || status.status === 'failed') {
            resolve(status);
          } else if (attempts >= maxAttempts) {
            reject(new Error('Processing timeout'));
          } else {
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          if (attempts >= maxAttempts) {
            reject(error);
          } else {
            setTimeout(poll, intervalMs);
          }
        }
      };

      poll();
    });
  }

  /**
   * Batch upload multiple documents
   */
  async batchUploadDocuments(startupId, files) {
    const uploadPromises = files.map(({ file, documentType }) =>
      this.uploadDocument(startupId, file, documentType)
    );

    try {
      const results = await Promise.allSettled(uploadPromises);
      return {
        successful: results.filter(r => r.status === 'fulfilled').map(r => r.value),
        failed: results.filter(r => r.status === 'rejected').map(r => r.reason),
      };
    } catch (error) {
      throw new Error('Batch upload failed');
    }
  }

  /**
   * Get complete startup data (profile + documents + status)
   */
  async getCompleteStartupData(startupId) {
    try {
      const [profile, documents, status] = await Promise.all([
        this.getStartupProfile(startupId),
        this.getStartupDocuments(startupId),
        this.getStartupStatus(startupId),
      ]);

      return {
        profile,
        documents,
        status,
        startupId,
      };
    } catch (error) {
      throw new Error(`Failed to fetch complete startup data: ${error.message}`);
    }
  }

  // =================== ADDITIONAL METHODS FROM UNIFIED SERVICE ===================

  /**
   * Export profile with options
   */
  async exportProfile(startupId, exportOptions = {}) {
    return this.makeRequest(`/startups/${startupId}/export`, {
      method: 'POST',
      body: JSON.stringify(exportOptions),
    });
  }

  /**
   * Trigger AI suggestions analysis (async with Celery)
   * Returns task_id immediately, then poll for status
   */
  async triggerAISuggestionsAnalysis(startupId, section = null) {
    const url = section
      ? `/startups/${startupId}/ai-suggestions/${section}`
      : `/startups/${startupId}/ai-suggestions`;

    return this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  /**
   * Check AI suggestions analysis status
   */
  async getAISuggestionsStatus(startupId) {
    return this.makeRequest(`/startups/${startupId}/ai-suggestions/status`);
  }

  /**
   * Get completed AI suggestions results
   */
  async getAISuggestionsResults(startupId) {
    return this.makeRequest(`/startups/${startupId}/ai-suggestions/results`);
  }

  /**
   * Poll for AI suggestions completion
   * @param {number} startupId - Startup ID
   * @param {number} maxAttempts - Maximum polling attempts (default: 60)
   * @param {number} intervalMs - Polling interval in ms (default: 2000)
   * @returns {Promise} Resolves with results when complete
   */
  async pollAISuggestionsStatus(startupId, maxAttempts = 60, intervalMs = 2000) {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;
          const status = await this.getAISuggestionsStatus(startupId);

          if (status.status === 'completed') {
            // Get the actual results
            const results = await this.getAISuggestionsResults(startupId);
            resolve(results);
          } else if (status.status === 'failed') {
            reject(new Error(status.error || 'AI analysis failed'));
          } else if (attempts >= maxAttempts) {
            reject(new Error('AI analysis timeout - please try again'));
          } else {
            // Still processing, poll again
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          if (attempts >= maxAttempts) {
            reject(error);
          } else {
            // Retry on error
            setTimeout(poll, intervalMs);
          }
        }
      };

      poll();
    });
  }

  /**
   * LEGACY: Get AI suggestions (synchronous-style wrapper for backward compatibility)
   * This triggers analysis and polls for completion automatically
   */
  async getAISuggestions(startupId, sections = null) {
    try {
      // First, try to get existing results
      const existingResults = await this.getAISuggestionsResults(startupId).catch(() => null);
      if (existingResults && existingResults.success) {
        return existingResults;
      }
    } catch (e) {
      // No existing results, continue to trigger new analysis
    }

    // Trigger new analysis
    await this.triggerAISuggestionsAnalysis(startupId, sections);

    // Poll for completion
    return this.pollAISuggestionsStatus(startupId);
  }

  /**
   * LEGACY: Get AI suggestions for a specific section
   */
  async getAISuggestionsForSection(startupId, section) {
    try {
      // First, try to get existing results
      const existingResults = await this.getAISuggestionsResults(startupId).catch(() => null);
      if (existingResults && existingResults.success) {
        return existingResults;
      }
    } catch (e) {
      // No existing results, continue to trigger new analysis
    }

    // Trigger new analysis for section
    await this.triggerAISuggestionsAnalysis(startupId, section);

    // Poll for completion
    return this.pollAISuggestionsStatus(startupId);
  }

  // ================= MENTOR ASSIGNMENT API (Refactored MVP) =================
  async getMentors(centerId = null) {
    const params = centerId ? `?center_id=${centerId}` : '';
    return this.makeRequest(`/mentors${params}`);
  }

  async createMentorAssignment(data) {
    return this.makeRequest('/mentors/assignments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMentorAssignments(filters = {}) {
    const params = new URLSearchParams();
    if (filters.startup_id) params.append('startup_id', filters.startup_id);
    if (filters.mentor_id) params.append('mentor_id', filters.mentor_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.center_id) params.append('center_id', filters.center_id);
    const queryString = params.toString();
    const endpoint = `/mentors/assignments${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint);
  }

  // Get mentor suggestions for a startup (rule-based matching)
  async getMentorSuggestions(startupId, limit = 10, minScore = 40) {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('min_score', minScore);
    return this.makeRequest(`/mentors/startups/${startupId}/suggestions?${params}`);
  }

  // Get startups without active mentor assignments
  async getUnassignedStartups(centerId = null) {
    const params = centerId ? `?center_id=${centerId}` : '';
    return this.makeRequest(`/mentors/startups/unassigned${params}`);
  }

  // Update assignment status (handles all workflow actions)
  async updateAssignmentStatus(assignmentId, statusData) {
    return this.makeRequest(`/mentors/assignments/${assignmentId}`, {
      method: 'PATCH',
      body: JSON.stringify(statusData),
    });
  }

  // Helper methods for common status updates
  async respondToAssignment(assignmentId, status, rejectionReason = null) {
    const payload = { status };
    if (status === 'REJECTED' && rejectionReason) {
      payload.rejection_reason = rejectionReason;
    }
    return this.updateAssignmentStatus(assignmentId, payload);
  }

  async submitFeedback(assignmentId, rating, comment = null) {
    return this.updateAssignmentStatus(assignmentId, {
      status: 'COMPLETED',
      feedback_rating: rating,
      feedback_comment: comment,
    });
  }

  async escalateAssignment(assignmentId, escalationReason) {
    return this.updateAssignmentStatus(assignmentId, {
      status: 'ESCALATED',
      escalation_reason: escalationReason,
    });
  }

  async reassignAssignment(assignmentId, newMentorId) {
    return this.updateAssignmentStatus(assignmentId, {
      status: 'REASSIGNED',
      reassignment_mentor_id: newMentorId,
    });
  }

  // ================= WORKLOAD MANAGEMENT API =================
  async getWorkloadDashboard(centerId = null) {
    const params = centerId ? `?center_id=${centerId}` : '';
    return this.makeRequest(`/mentors/workload/dashboard${params}`);
  }

  async getWorkloadHistory(centerId = null, days = 90) {
    const params = new URLSearchParams();
    if (centerId) params.append('center_id', centerId);
    params.append('days', days);
    return this.makeRequest(`/mentors/workload/history?${params}`);
  }

  async getWorkloadSummary(centerId = null) {
    const params = centerId ? `?center_id=${centerId}` : '';
    return this.makeRequest(`/mentors/workload/summary${params}`);
  }

  async getPerformanceSummary(centerId = null) {
    const params = centerId ? `?center_id=${centerId}` : '';
    return this.makeRequest(`/mentors/performance/summary${params}`);
  }

  async getWorkloadAlerts(centerId = null) {
    const params = centerId ? `?center_id=${centerId}` : '';
    return this.makeRequest(`/mentors/workload/alerts${params}`);
  }

  async updateMentorCapacity(userId, capacity) {
    return this.makeRequest(`/mentors/${userId}/capacity`, {
      method: 'PUT',
      body: JSON.stringify({ capacity }),
    });
  }

  // ================= CONFLICT MANAGEMENT API =================
  async createConflict(data) {
    return this.makeRequest('/mentors/conflicts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ================= MENTOR VERIFICATION API =================
  // Story 1.3: Expertise Validation and Verification (Minimal MVP)

  async getMentorsForVerification(filters = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.sortColumn) params.append('sortColumn', filters.sortColumn);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

    const queryString = params.toString();
    return this.makeRequest(`/mentors/verification${queryString ? `?${queryString}` : ''}`);
  }

  async getMentorVerificationDetails(mentorId) {
    return this.makeRequest(`/mentors/verification/${mentorId}`);
  }

  async updateMentorVerification(mentorId, verificationData) {
    return this.makeRequest(`/mentors/verification/${mentorId}`, {
      method: 'PUT',
      body: JSON.stringify(verificationData),
    });
  }

  // =================== MILESTONE TEMPLATE METHODS ===================
  async getMilestoneTemplates(filters = {}) {
    const params = new URLSearchParams();
    if (filters.center_id) params.append('center_id', filters.center_id);
    if (filters.category) params.append('category', filters.category);
    if (filters.program_type) params.append('program_type', filters.program_type);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
    if (filters.include_global !== undefined) params.append('include_global', filters.include_global);

    const query = params.toString();
    return this.makeRequest(`/milestones/milestone-templates${query ? `?${query}` : ''}`);
  }

  async createMilestoneTemplate(data) {
    return this.makeRequest('/milestones/milestone-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMilestoneTemplate(templateId) {
    return this.makeRequest(`/milestones/milestone-templates/${templateId}`);
  }

  async updateMilestoneTemplate(templateId, data) {
    return this.makeRequest(`/milestones/milestone-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMilestoneTemplate(templateId) {
    return this.makeRequest(`/milestones/milestone-templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  // =================== MILESTONE METHODS ===================
  async getStartupMilestones(startupId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);

    const query = params.toString();
    return this.makeRequest(`/milestones/${startupId}/milestones${query ? `?${query}` : ''}`);
  }

  async createMilestone(startupId, data) {
    return this.makeRequest(`/milestones/${startupId}/milestones`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMilestone(milestoneId) {
    return this.makeRequest(`/milestones/milestones/${milestoneId}`);
  }

  async updateMilestone(milestoneId, data) {
    return this.makeRequest(`/milestones/milestones/${milestoneId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMilestone(milestoneId) {
    return this.makeRequest(`/milestones/milestones/${milestoneId}`, {
      method: 'DELETE',
    });
  }

  // =================== CLEANUP ===================
  cleanup() {
    // Clear all pending timeouts
    for (const timeoutId of this.timeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.timeouts.clear();
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;

// Named exports for specific service groups
export const startupApi = {
  register: (data) => apiService.registerStartup(data),
  getProfile: (id) => apiService.getStartupProfile(id),
  updateSection: (id, section, data) => apiService.updateProfileSection(id, section, data),
  submit: (id) => apiService.submitStartup(id),
  submitProfile: (id, data) => apiService.submitProfile(id, data),
  getIncomplete: () => apiService.getIncompleteStartups(),
  resubmitProfile: (id, data) => apiService.resubmitProfile(id, data),
  getStatus: (id) => apiService.getStartupStatus(id),
  getAnalytics: (id) => apiService.getStartupAnalytics(id),
  getRequiredDocs: () => apiService.getDocumentRequirements?.(),
  getSessions: (id, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.upcoming) params.append('upcoming', filters.upcoming);
    const queryString = params.toString();
    return apiService.makeRequest(`/startups/${id}/sessions${queryString ? `?${queryString}` : ''}`);
  },
};

export const milestoneTemplateApi = {
  getAll: (filters) => apiService.getMilestoneTemplates(filters),
  create: (data) => apiService.createMilestoneTemplate(data),
  get: (id) => apiService.getMilestoneTemplate(id),
  update: (id, data) => apiService.updateMilestoneTemplate(id, data),
  delete: (id) => apiService.deleteMilestoneTemplate(id),
};

export const milestoneApi = {
  getByStartup: (startupId, filters) => apiService.getStartupMilestones(startupId, filters),
  create: (startupId, data) => apiService.createMilestone(startupId, data),
  get: (id) => apiService.getMilestone(id),
  update: (id, data) => apiService.updateMilestone(id, data),
  delete: (id) => apiService.deleteMilestone(id),
};

export const documentApi = {
  getAll: (startupId) => apiService.getStartupDocuments(startupId),
  getTemplates: (centerId) => apiService.getDocumentTemplates(centerId),
  // Use uploadDocument so we support progress callbacks and the
  // (startupId, file, documentType, onProgress) signature expected
  // by components like DocumentUpload.
  upload: (startupId, file, documentType, onProgress) =>
    apiService.uploadDocument(startupId, file, documentType, onProgress),
  delete: (startupId, documentId) => apiService.deleteStartupDocument(startupId, documentId),
  verify: (startupId, docId, data) => apiService.verifyDocument(startupId, docId, data),
  export: (startupId, options) => apiService.exportComplianceDocuments(startupId, options),
  getRequirements: (centerId) => apiService.getDocumentRequirements(centerId),
  generate: (startupId, options) => apiService.generateComplianceDocument(startupId, options),
};

export const aiApi = {
  uploadPitchDeck: (startupId, file) => apiService.uploadPitchDeck(startupId, file),
  getProcessingStatus: (startupId) => apiService.getPitchDeckProcessingStatus(startupId),
  pollStatus: (startupId, maxAttempts, interval) =>
    apiService.pollProcessingStatus(startupId, maxAttempts, interval),
};

export const centerApi = {
  getBranding: (id) => apiService.getCenterBranding(id),
  list: () => apiService.listCenters(),
};

export const exportApi = {
  profile: (startupId, options) => apiService.exportProfile(startupId, options),
};

// Enhanced named exports with all functionality
export const enhancedStartupApi = {
  ...startupApi,
  submitProfile: (id, data) => apiService.submitProfile(id, data),
  resubmitProfile: (id, data) => apiService.resubmitProfile(id, data),
  getCompleteData: (id) => apiService.getCompleteStartupData(id),
};

export const enhancedDocumentApi = {
  ...documentApi,
  batchUpload: (startupId, files) => apiService.batchUploadDocuments(startupId, files),
};

export const enhancedAiApi = {
  ...aiApi,
  getSuggestions: (startupId) => apiService.getAISuggestions(startupId),
  triggerAnalysis: (startupId, section) => apiService.triggerAISuggestionsAnalysis(startupId, section),
  getStatus: (startupId) => apiService.getAISuggestionsStatus(startupId),
  getResults: (startupId) => apiService.getAISuggestionsResults(startupId),
  pollStatus: (startupId, maxAttempts, interval) => apiService.pollAISuggestionsStatus(startupId, maxAttempts, interval),
};

export const mentorApi = {
  getMentors: (centerId) => apiService.getMentors(centerId),

  // Assignment Management (Refactored MVP)
  createAssignment: (data) => apiService.createMentorAssignment(data),
  getAssignments: (filters) => apiService.getMentorAssignments(filters),
  getAssignment: (id) => apiService.makeRequest(`/mentors/assignments/${id}`),
  getMentorSuggestions: (startupId, limit, minScore) => apiService.getMentorSuggestions(startupId, limit, minScore),
  getUnassignedStartups: (centerId) => apiService.getUnassignedStartups(centerId),
  updateAssignmentStatus: (assignmentId, statusData) => apiService.updateAssignmentStatus(assignmentId, statusData),
  respondToAssignment: (assignmentId, status, reason) => apiService.respondToAssignment(assignmentId, status, reason),
  submitFeedback: (assignmentId, rating, comment) => apiService.submitFeedback(assignmentId, rating, comment),
  escalateAssignment: (assignmentId, reason) => apiService.escalateAssignment(assignmentId, reason),
  reassignAssignment: (assignmentId, newMentorId) => apiService.reassignAssignment(assignmentId, newMentorId),

  // Conflict Management
  createConflict: (data) => apiService.createConflict(data),

  // Workload Management
  getWorkloadDashboard: (centerId) => apiService.getWorkloadDashboard(centerId),
  getWorkloadHistory: (centerId, days) => apiService.getWorkloadHistory(centerId, days),
  getWorkloadSummary: (centerId) => apiService.getWorkloadSummary(centerId),
  getPerformanceSummary: (centerId) => apiService.getPerformanceSummary(centerId),
  getWorkloadAlerts: (centerId) => apiService.getWorkloadAlerts(centerId),
  updateCapacity: (userId, capacity) => apiService.updateMentorCapacity(userId, capacity),

  // Performance
  getPerformanceReport: (centerId, startDate, endDate) => apiService.getPerformanceReport(centerId, startDate, endDate),
  getPeerEvaluationsSummary: (centerId) => apiService.getPeerEvaluationsSummary(centerId),
  getImprovementPlans: (centerId, startDate, endDate) => apiService.getImprovementPlans(centerId, startDate, endDate),

  // Verification (Minimal MVP)
  getMentorsForVerification: (filters) => apiService.getMentorsForVerification(filters),
  getMentorVerificationDetails: (mentorId) => apiService.getMentorVerificationDetails(mentorId),
  updateMentorVerification: (mentorId, verificationData) => apiService.updateMentorVerification(mentorId, verificationData),

  // Community Features
  getPeerMentoringMatches: (mentorId, minYears = 3) => {
    return apiService.makeRequest(`/mentors/community/peer-mentoring/matches?mentor_id=${mentorId}&min_years=${minYears}`);
  },
  getBestPractices: (filters) => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page);
    if (filters?.limit) params.append('limit', filters.limit);
    const queryString = params.toString();
    return apiService.makeRequest(`/mentors/community/best-practices${queryString ? `?${queryString}` : ''}`);
  },
  getNetworkingEvents: (filters) => {
    const params = new URLSearchParams();
    if (filters?.center_id) params.append('center_id', filters.center_id);
    if (filters?.upcoming_only !== undefined) params.append('upcoming_only', filters.upcoming_only);
    if (filters?.page) params.append('page', filters.page);
    if (filters?.limit) params.append('limit', filters.limit);
    const queryString = params.toString();
    return apiService.makeRequest(`/mentors/community/networking-events${queryString ? `?${queryString}` : ''}`);
  },
  getCollaborationOpportunities: (mentorId, centerId) => {
    const params = new URLSearchParams();
    if (mentorId) params.append('mentor_id', mentorId);
    if (centerId) params.append('center_id', centerId);
    const queryString = params.toString();
    return apiService.makeRequest(`/mentors/community/collaboration/opportunities${queryString ? `?${queryString}` : ''}`);
  },
  getRecognizedMentors: (filters) => {
    const params = new URLSearchParams();
    if (filters?.center_id) params.append('center_id', filters.center_id);
    if (filters?.period) params.append('period', filters.period);
    if (filters?.limit) params.append('limit', filters.limit);
    const queryString = params.toString();
    return apiService.makeRequest(`/mentors/community/recognition${queryString ? `?${queryString}` : ''}`);
  },
  submitMentorFeedback: (reviewedMentorId, rating, comment, collaborationContext) => {
    return apiService.makeRequest('/mentors/community/feedback', {
      method: 'POST',
      body: JSON.stringify({
        reviewed_mentor_id: reviewedMentorId,
        rating: rating,
        comment: comment,
        collaboration_context: collaborationContext
      })
    });
  },
  getMentorPeerFeedback: (mentorId) => {
    return apiService.makeRequest(`/mentors/community/feedback/${mentorId}`);
  },

  // Session Management
  createSession: (data) => {
    return apiService.makeRequest('/mentors/sessions', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  updateSession: (sessionId, data) => {
    return apiService.makeRequest(`/mentors/sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },
  getMentorProfile: (mentorId) => apiService.makeRequest(`/mentors/profile/${mentorId}`),
};

// Export API_BASE_URL for components that need it
export { API_BASE_URL };

// Auto-cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    apiService.cleanup();
  });
}
